import React, { memo, useMemo, useCallback, useEffect, useState } from "react";
import {
  MenuItem,
  SubItem,
} from "@dreambigger/design-system/src/components/organisms/nav-bar";
import { Nav, Footer, DefaultAppLayout } from "@dreambigger/design-system";
import cotributeDefaults from "@dreambigger/shared/src/assets/cotribute-defaults";
import { useRouter } from "next/router";
import useUser from "@dreambigger/shared/src/hooks/use-user";
import { hasCompanyEventPlannerRole } from "@dreambigger/shared/src/utils/user";
import { removeLogoutLocalStorageItems } from "@dreambigger/shared/src/utils/local-storage";
import {
  api,
  tokenHelper,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import { OrgBootstrap } from "@dreambigger/shared/src/types";
import useMixpanel from "../../hooks/use-mixpanel";
import InspireAnalytics from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { removeInspireCookies } from "../../utils/cookies";
import { useGA, useGTM, useSegment } from "@dreambigger/shared/src/hooks";

// In community board, InspireLayout is used in the <BoardWrapper> component.
const InspireLayout = ({
  orgBootstrap,
  children,
  fixed,
  preFooterContent,
}: {
  orgBootstrap: OrgBootstrap;
  children: React.ReactChild;
  fixed?: boolean;
  preFooterContent?: React.ReactNode;
}) => {
  const [gaReady, setGaReady] = useState(false);
  const router = useRouter();
  const logo = orgBootstrap.logo || cotributeDefaults.logo;
  const slug = orgBootstrap.slug || "cotribute";
  const gtmContainerId = orgBootstrap.gtmContainerId;
  const eventsLabel = orgBootstrap?.keywordUpper
    ? `${orgBootstrap.keywordUpper}s`
    : "Events";
  const [userLoading, { data: user }] = useUser(api, tokenHelper);
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();
  const googleTagManager = useGTM();

  // Initialize google analytics. Ensure this only happens once.
  useEffect(() => {
    if (!gaReady) {
      googleAnalytics.initialize(undefined, { debug: false });
      // Define custom dimensions for custom reports. Learn more in file "use-ga.tsx".
      setGaReady(true);
    }
  }, [googleAnalytics]);

  // Initialize GTM
  useEffect(() => {
    if (gtmContainerId) {
      googleTagManager.initialize(gtmContainerId);
    }
  }, [gtmContainerId]);

  // Record page view in GTM
  useEffect(() => {
    if (!router.isReady) return;
    googleTagManager.push({
      event: "page viewed",
      page: router.asPath.split("?")[0],
      queryParams: router.query,
      fullUrl: router.asPath,
    });
  }, [router.asPath]);

  // Hide the sign in button if the community doesn't allow it.
  const hideSignIn = orgBootstrap?.hideSignInExternalWeb;

  // Get the about link from the "moreDropdownButtonLinks" if it exists.
  let aboutLink = "";
  if (
    orgBootstrap.moreDropdownButtonLinks &&
    orgBootstrap.moreDropdownButtonLinks.length
  ) {
    const aboutButton = orgBootstrap.moreDropdownButtonLinks.find(
      (link) => link.title === "About"
    );

    aboutLink = aboutButton?.action || "";
  }

  const homepage =
    (process?.browser &&
      window.location.origin + `/community/${slug}/detail`) ||
    undefined;

  const menuItems = useMemo(() => {
    return [
      orgBootstrap.hasActiveCampaigns && {
        key: "campaigns",
        name: eventsLabel,
        isSelected: [
          "/events/[eventId]/detail",
          "/community/[communitySlug]/events",
        ].includes(router.pathname),
        onClick: () => {
          router.push(`/community/${slug}/events`);
        },
      },
      orgBootstrap.hasActiveStories && {
        key: "stories",
        name: "Stories",
        isSelected: router.pathname === "/community/[communitySlug]/stories",

        onClick: () => {
          router.push(`/community/${slug}/stories`);
        },
      },
      orgBootstrap.hasActiveCommunities && {
        key: "communities",
        name: "Communities",
        isSelected:
          router.pathname === "/community/[communitySlug]/communities",

        onClick: () => {
          router.push(`/community/${slug}/communities`);
        },
      },
      orgBootstrap.moreDropdownButtonLinks &&
        orgBootstrap.moreDropdownButtonLinks.length > 0 && {
          key: "more",
          name: "More",
          subItems: orgBootstrap.moreDropdownButtonLinks.map((link) => ({
            key: link.title,
            name: link.title,
            icon: link.icon,
            onClick: () => {
              link.target === "_blank"
                ? window.open(link.action, "_blank")?.focus()
                : router.push(link.action);
            },
          })),
        },
    ].filter(Boolean) as MenuItem[];
  }, [orgBootstrap, router.pathname]);

  const signInCallback = useCallback(() => {
    window.location.pathname = `/authenticate/${orgBootstrap.slug}`;
  }, [orgBootstrap.slug]);

  const onLogoClick = useCallback(() => {
    if (homepage) window.location.href = homepage;
  }, []);

  // Variables needed for <AlgoliaAutoCompleteSearch> in <NavBar>.
  const searchIndexName = `community_board_${process.env.NEXT_PUBLIC_ALGOLIA_ENV}_autocomplete_search`;
  const searchUrlPathname = `/community/${slug}/search`;

  const userMenuItems = useMemo(() => {
    return [
      {
        key: "my-events",
        name: "My Events",
        onClick: () => (window.location.pathname = "/user/events"),
      },
      user?.organization?.roles &&
        hasCompanyEventPlannerRole(user?.organization?.roles) && {
          key: "create-event",
          name: "Create an Event",
          onClick: () =>
            (window.location.pathname = `/share/community/${tokenHelper.getCommunitySlug()}`),
        },
      {
        key: "sign-out",
        name: "Sign Out",
        onClick: () => {
          removeLogoutLocalStorageItems();

          removeInspireCookies();

          InspireAnalytics.reset({
            segment,
            mixpanel,
          });

          router.reload();
        },
      },
    ].filter(Boolean) as SubItem[];
  }, []);

  const nav = useMemo(
    () => (
      <Nav
        hideSignIn={hideSignIn}
        showFunds={orgBootstrap.externalMoneyBuckets}
        user={user}
        userLoading={userLoading}
        userMenuItems={userMenuItems}
        logo={{
          src: logo,
          onClick: onLogoClick,
        }}
        menuItems={menuItems}
        communityUrl={`community/${slug}/detail`}
        signInCallback={signInCallback}
        searchIndexName={searchIndexName}
        searchUrlPathname={searchUrlPathname}
        searchFilters={`(type:event OR type:fundraiser OR type:community OR type:collection) AND organizationId:${orgBootstrap.id}`}
      />
    ),
    [
      user,
      logo,
      menuItems,
      userMenuItems,
      userLoading,
      orgBootstrap,
      onLogoClick,
      searchUrlPathname,
      signInCallback,
    ]
  );
  const footer = useMemo(
    () => (
      <>
        {preFooterContent}
        <Footer
          logo={logo}
          name={orgBootstrap.name || "Cotribute"}
          privacyLink={
            orgBootstrap.clientPrivacyPolicy || cotributeDefaults.privacyPolicy
          }
          termsLink={orgBootstrap.termsOfUse || cotributeDefaults.terms}
          supportPhone={orgBootstrap.phone}
          supportEmail={orgBootstrap.email}
          homepage={homepage || "#"}
          aboutLink={aboutLink}
        />
      </>
    ),
    [
      preFooterContent,
      logo,
      orgBootstrap,
      homepage,
      aboutLink,
      cotributeDefaults,
    ]
  );

  return (
    <DefaultAppLayout
      nav={nav}
      footer={footer}
      fixed={fixed}
      backgroundColor="
      #fafafa"
    >
      {children}
    </DefaultAppLayout>
  );
};

export default memo(InspireLayout);
